import { useEffect, useMemo, useState } from 'react'
import { Portal } from '@headlessui/react'
import { AnimatePresence, motion } from 'framer-motion'
import { Configure, InstantSearch, useSearchBox, useInstantSearch, useInfiniteHits } from 'react-instantsearch'
import { liteClient } from 'algoliasearch/lite'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'

import Insights from '@/components/search/insights'
import Autocomplete from '@/components/search/autocomplete'
import InfiniteHits from '@/components/search/infinite-hits'
import routing from '@/components/search/routing'

import CloseIcon from '@/icons/bwi/close.svg'
import EnterKeyIcon from '@/icons/bwi/key-enter.svg'
import UpKeyIcon from '@/icons/bwi/key-arrow-up.svg'
import DownKeyIcon from '@/icons/bwi/key-arrow-down.svg'
import EscKeyIcon from '@/icons/bwi/key-escape.svg'

import { getLocale, useTranslations } from '@/utils/locale'
import useScrollLock from '@/utils/useScrollLock'
import { cx } from '@/utils/strings'
import { HitProps } from './types'


type SearchModalProps = {
  isOpen?: boolean
  onClose?: () => void
  /** Search input placeholder */
  placeholder?: string
  /** Restrict search to a single type */
  searchType?: string
}

export default function SearchModal(props: SearchModalProps) {
  const { t } = useTranslations()
  const { placeholder = t('What are you looking for?'), isOpen = false, onClose, searchType } = props
  const { VITE_ALGOLIA_APP_ID, VITE_ALGOLIA_SEARCH_KEY, VITE_ALGOLIA_INDEX_PREFIX } = import.meta.env
  const indexName = VITE_ALGOLIA_INDEX_PREFIX + getLocale().toLocaleLowerCase().replace('-', '_')

  const searchClient = useMemo(() => liteClient(VITE_ALGOLIA_APP_ID, VITE_ALGOLIA_SEARCH_KEY), [])

  const handleClose = () => {
    // Remove search query from URL
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('q')
    window.history.replaceState({}, '', `${window.location.pathname}${searchParams.toString()}`)

    onClose?.()
  }

  // Add keyboard shortcuts
  // - Close search modal with esc
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        handleClose()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return (): void => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])

  // Scroll lock page when the modal is open
  const { lock, unlock } = useScrollLock({ autoLock: false })
  useEffect(() => {
    if (isOpen) {
      lock()
    } else {
      unlock()
    }

    return () => {
      unlock()
    }
  }, [isOpen])

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={cx('fixed inset-0 z-50 py-8 lg:py-16 overflow-hidden bg-indigoBlue/90 dark:bg-secondary-900/90')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={handleClose}
          >
            <motion.div
              className={cx(
                'w-full max-w-screen-2xl md:mx-auto px-4 md:px-8 flex flex-col justify-between h-full max-h-screen'
              )}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2, ease: 'anticipate' }}
            >
              <button
                type="button"
                className="absolute top-4 right-4 z-10 2xl:top-3 2xl:right-3 md:right-12 text-white"
              >
                <CloseIcon
                  title="Close search"
                  className="text-white w-9 h-9 p-2"
                  type="button"
                  onClick={handleClose}
                />
              </button>
              <InstantSearch searchClient={searchClient} indexName={indexName} routing={routing({ indexName })}>
                <Configure
                  filters={searchType && `type:${searchType}`}
                  attributesToRetrieve={[
                    'type',
                    'category',
                    'url',
                    'hash',
                    'title',
                    'body',
                    'objectID',
                    'breadcrumbs',
                    'toc'
                  ]}
                />
                <SearchModalContent
                  searchClient={searchClient}
                  indexName={indexName}
                  placeholder={placeholder}
                  searchType={searchType}
                  handleClose={handleClose}
                />
              </InstantSearch>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  )
}

type SearchModalContentProps = {
  searchClient: any
  indexName: string
  placeholder: string
  searchType?: string
  handleClose: () => void
}

// Purpose separating the content into another component is to be to access context/hooks from InstantSearch
function SearchModalContent({ searchClient, indexName, placeholder, searchType, handleClose }: SearchModalContentProps) {
  const { t } = useTranslations()
  const { query } = useSearchBox()
  const { currentPageHits } = useInfiniteHits()
  const { status } = useInstantSearch()
  const recentSearches = useMemo(() => createLocalStorageRecentSearchesPlugin({
    key: 'instantsearch',
    limit: 5,
  }), [])

  const [activeHit, setActiveHit] = useState<HitProps | null>(null)

  return (
    <>
      <Configure distinct={4} clickAnalytics />
      <Insights />
      <div
        className={cx(
          'flex-none mt-8 lg:mt-0',
          (query.length !== 0 || status === 'error') && '[&_.aa-Form]:!rounded-b-none [&_.aa-Panel]:!hidden'
        )}
        style={
          {
            '--aa-search-input-height': '4rem',
            '--aa-input-icon-size': '1.5rem',
          } as any
        }
      >
        <Autocomplete
          classNames={{
            form: '!rounded-2xl !border-t-0 !border-l-0 !border-r-0 !border-gray-100 dark:!border-secondary-700 !border-b-2 focus-within:!outline-none focus-within:!shadow-none',
            inputWrapper: 'pl-2',
            input: 'md:!text-2xl md:placeholder:!text-2xl pl-8',
            submitButton: '!pl-4',
            panel: '!relative !-top-4 !left-0 !right-auto !shadow-none !rounded-none !rounded-b-2xl',
            panelLayout: '!p-4',
            sourceHeader: '!px-2',
            clearButton:
              '!hidden',
              // 'w-12 h-12 [&_.aa-ClearIcon]:!w-6 [&_.aa-ClearIcon]:!h-6 w-12 h-12 [&_.aa-ClearIcon]:!w-6 [&_.aa-ClearIcon]:!h-6',
            bwSearchContainer: 'mt-0',
            bwPanelContainer: 'mt-0 ',
          }}
          searchClient={searchClient}
          placeholder={placeholder}
          indexName={indexName}
          detachedMediaQuery="none"
          shouldPanelOpen={({ state }) => state.query.length === 0}
          updateOnChange
          openOnFocus
        />
      </div>
      <InfiniteHits
        className={cx(
          'bg-white dark:bg-secondary-800 flex-1 overflow-hidden rounded-b-2xl md:rounded-b-none',
          currentPageHits.length === 0 && 'rounded-b-2xl!'
        )}
        variant="preview"
        shouldGroupResults
        showIcons={false}
        onLinkClick={(hit) => {
          // If the hit has a hash and the current page is the same as the hit's URL, close the modal
          if (window.location.pathname === hit.url && hit.hash) {
            handleClose()
          }
          // Add query to recent searches
          recentSearches.data?.addItem({
            id: query,
            label: query,
          })
        }}
        onLinkPreview={(hit) => {
          setActiveHit(hit)
        }}
      />
      <div
        className={cx(
          'hidden md:flex min-h-18 bg-white dark:bg-secondary-800 rounded-b-2xl items-center justify-between px-6 py-3 border-t-2 border-gray-100 dark:border-secondary-700',
          (query.length === 0 || currentPageHits.length === 0) && 'md:hidden'
        )}
      >
        <div className="flex gap-8 text-xl text-gray-500 dark:text-white pl-5">
          <div className="flex items-center justify-center">
            <EnterKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('select')}</span>
          </div>
          <div className="flex items-center justify-center">
            <UpKeyIcon className="fill-none w-7 h-7 mr-1" />
            <DownKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('navigate')}</span>
          </div>
          <div className="flex items-center justify-center">
            <EscKeyIcon className="fill-none w-7 h-7 mr-3" />
            <span>{t('close')}</span>
          </div>
        </div>
      </div>
    </>
  )
}
